import clsx from 'clsx';
import type { ReactElement } from 'react';

import './SDSIcon.scss';

export type SDSIconSize =
  | 'x-small'
  | 'small'
  | 'medium'
  | 'big'
  | 'large'
  | 'x-large'
  | 'icons'
  | 'typing-text-regular';

type SDSBrandIconColor = 'primary' | 'secondary' | 'tertiary' | 'quaternary';
type SDSStatusIconColor = 'alert' | 'info' | 'warning';
type SDSRoleStatusIconColor = 'status-admin' | 'status-agent' | 'status-mentor' | 'status-student';
type SDSRegularIconColor = 'text-primary' | 'text-secondary' | 'inactive' | 'muted' | 'on-brand';
export type SDSIconColor = SDSBrandIconColor | SDSStatusIconColor | SDSRoleStatusIconColor | SDSRegularIconColor;

export interface SDSIconProps {
  icon: ReactElement;
  size?: SDSIconSize;
  color?: SDSIconColor;
  className?: string;
  /** If provided, icon is meaningful and announced to screen readers. If omitted, icon is decorative. */
  ariaLabel?: string;
  /**
   * A handle on the icon itself, for a state only the icon carries.
   *
   * Lands on the element the icon already renders, so asking for one adds an
   * attribute and nothing else — no wrapper, no change to the layout.
   */
  dataTestId?: string;
}

export function SDSIcon({ icon, size = 'medium', color, className, ariaLabel, dataTestId }: SDSIconProps) {
  const iconClassName = clsx('SDSIcon', `SDSIcon--${size}`, color && `SDSIcon--${color}`, className);

  if (ariaLabel) {
    return (
      <span className={iconClassName} data-testid={dataTestId}>
        <span aria-hidden="true">{icon}</span>
        <span className="SDSIcon__accessible-label">{ariaLabel}</span>
      </span>
    );
  }

  return (
    <span className={iconClassName} aria-hidden="true" data-testid={dataTestId}>
      {icon}
    </span>
  );
}
